import {AutoLayoutHelpers, getStackupLayersAndViaConfigs} from "@buildwithflux/core";
import {
    AnyPcbBakedNode,
    AnyPcbNode,
    AutoLayoutIterationNode,
    AutoLayoutIterationSmartViaNode,
    AutoLayoutIterationTraceNode,
    DEFAULT_THROUGH_HOLE_VIA_UID,
    IDocumentData,
    IPcbBoardLayerCopper,
    LayerOrientation,
    PcbNodeTypes,
    PcbNodesMap,
    PcbVirtualNode,
    createDiminishedNode,
    createVirtualRouteSegmentNode,
    createVirtualSmartViaNode,
    matchLayerIdentifier,
} from "@buildwithflux/models";
import {guid, humanizeLengthValue, humanizeVectorValue} from "@buildwithflux/shared";

/**
 * Retrieves all auto-layout node UIDs from a document.
 * Auto-layout nodes are identified by their UID prefix.
 *
 * @param document - The document to search for auto-layout nodes
 * @returns Array of auto-layout node UIDs, or empty array if document has no PCB layout nodes
 */
export function getAutoLayoutNodeUidsInDocument(document: IDocumentData | null): string[] {
    if (!document?.pcbLayoutNodes) {
        return [];
    }

    const autoLayoutNodesUids: string[] = [];

    for (const nodeUid in document.pcbLayoutNodes) {
        if (nodeUid.startsWith(AutoLayoutHelpers.autoLayoutNodeUidPrefix)) {
            autoLayoutNodesUids.push(nodeUid);
        }
    }

    return autoLayoutNodesUids;
}

/**
 * Creates new PCB nodes from existing auto-layout nodes by copying their properties
 * and generating new UIDs. The created nodes are unlocked and have no children.
 *
 * For route segment nodes, new start and end IDs are generated based on the new UID.
 *
 * @param existingNodes - Array of existing auto-layout nodes to copy
 * @returns Array of new nodes with fresh UIDs and reset properties
 */
export function createNodesFromExistingAutoLayoutNodes(existingNodes: AnyPcbNode[]): AnyPcbNode[] {
    return existingNodes.map((node) => {
        const newNode: AnyPcbNode = {
            ...createDiminishedNode(node),
            uid: guid(),
            locked: false,
            childUids: [],
        };

        if (newNode.type === PcbNodeTypes.routeSegment) {
            newNode.startId = `${newNode.uid}_start`;
            newNode.endId = `${newNode.uid}_end`;
        }

        return newNode;
    });
}

/**
 * Creates virtual PCB nodes (traces and vias) from auto-layout iteration nodes.
 * Virtual nodes are used to preview auto-layout results before applying them to the document.
 *
 * The function processes iteration data and creates appropriately configured virtual nodes
 * with correct layer orientations, positions, and properties.
 *
 * @param pcbLayoutNodes - Map of all PCB nodes in the document
 * @param layoutNodeUid - UID of the layout node containing stackup configuration
 * @param iterationNodes - Array of auto-layout iteration nodes to convert
 * @returns Array of virtual trace and via nodes ready for preview
 * @throws Error if layout node is invalid or not found
 */
export function createAutoLayoutVirtualNodes(
    pcbLayoutNodes: PcbNodesMap<AnyPcbBakedNode>,
    layoutNodeUid: string,
    iterationNodes: AutoLayoutIterationNode[],
): PcbVirtualNode<PcbNodeTypes.smartVia | PcbNodeTypes.routeSegment>[] {
    const layoutNode = pcbLayoutNodes[layoutNodeUid];
    if (!layoutNode || layoutNode.type !== PcbNodeTypes.layout) {
        throw new Error(`Invalid layout node UID: ${layoutNodeUid}`);
    }

    const {copperLayers} = getStackupLayersAndViaConfigs(layoutNode.bakedRules, pcbLayoutNodes);

    return Object.values(iterationNodes).map((iterationNodeData) => {
        if (iterationNodeData.type === PcbNodeTypes.routeSegment) {
            return createVirtualTraceNode(pcbLayoutNodes, iterationNodeData, copperLayers);
        } else {
            return createSmartVirtualViaNode(pcbLayoutNodes, iterationNodeData);
        }
    });
}

function createVirtualTraceNode(
    pcbLayoutNodes: PcbNodesMap<AnyPcbBakedNode>,
    virtualTraceNodeData: AutoLayoutIterationTraceNode,
    copperLayers: IPcbBoardLayerCopper[],
): PcbVirtualNode<PcbNodeTypes.routeSegment> {
    const netNode = pcbLayoutNodes[virtualTraceNodeData.netUid];
    if (!netNode || netNode.type !== PcbNodeTypes.net) {
        throw new Error(`Invalid net node UID: ${virtualTraceNodeData.netUid}`);
    }

    const netLayer = copperLayers.find((layer) => matchLayerIdentifier(layer, netNode.bakedRules.layer));
    const netLayerOrientation = netLayer?.orientation ?? LayerOrientation.top;

    const {uid, layerId, startPosition, endPosition, traceShape, traceShapeStart, traceShapeEnd} =
        structuredClone(virtualTraceNodeData);

    const layer = copperLayers.find((layer) => matchLayerIdentifier(layer, layerId));
    if (!layer) {
        throw new Error(`Layer not found: ${layerId}`);
    }

    const traceLayerOrientation = layer.orientation ?? LayerOrientation.top;
    const parentBottom = netLayerOrientation === LayerOrientation.bottom;
    const traceBottom = traceLayerOrientation === LayerOrientation.bottom;
    const invertLayer = parentBottom === traceBottom ? 1 : -1;
    startPosition.x *= invertLayer;
    endPosition.x *= invertLayer;
    const startId = `${uid}_start`;
    const endId = `${uid}_end`;

    return createVirtualRouteSegmentNode(
        "autoLayout",
        uid,
        startId,
        endId,
        startPosition,
        endPosition,
        netNode.uid,
        layerId,
        // We're leaving the traceWidth undefined so the trace node can use the
        // derived traceWidth from the parent net node, or a ruleset
        undefined,
        undefined,
        layerId,
        "Trace",
        {locked: true},
        {
            traceShape,
            traceShapeStart,
            traceShapeEnd,
        },
    );
}

function createSmartVirtualViaNode(
    pcbLayoutNodes: PcbNodesMap<AnyPcbBakedNode>,
    virtualSmartViaNodeData: AutoLayoutIterationSmartViaNode,
): PcbVirtualNode<PcbNodeTypes.smartVia> {
    const netNodeUid = virtualSmartViaNodeData.netUid;
    const netNode = pcbLayoutNodes[netNodeUid];
    if (!netNode || netNode.type !== PcbNodeTypes.net) {
        throw new Error(`Invalid net node UID: ${netNodeUid}`);
    }

    const viaOptions = [virtualSmartViaNodeData.viaConfigUid];
    if (!viaOptions.includes(DEFAULT_THROUGH_HOLE_VIA_UID)) {
        viaOptions.push(DEFAULT_THROUGH_HOLE_VIA_UID);
    }
    return createVirtualSmartViaNode(
        "autoLayout",
        virtualSmartViaNodeData.uid,
        "Smart Via",
        netNodeUid,
        netNodeUid,
        {
            uid: virtualSmartViaNodeData.uid,
            parentUid: netNodeUid,
            locked: true,
        },
        {
            position: humanizeVectorValue(
                humanizeLengthValue,
                virtualSmartViaNodeData.position,
                netNode.bakedRules.unit ?? "mm",
            ),
            connectedLayers: "All",
            viaOptions: viaOptions.join(","),
        },
    );
}
